import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import type { Article } from '../../@types/ArticleV2';
import type { ElasticSearchResponse } from '../../@types/ElasticSearchResponse';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import type { ApiResponse } from '../../Http';
import { buildGetRequest } from '../../Http/requestTemplates';
import useHttp from '../../Http/useHttp';
import { ARTICLES_QUERY } from './QueryKeys';

const useArticles = (
  _searchParams?: URLSearchParams,
): [
  ApiResponse<ElasticSearchResponse<Article>> | undefined,
  RequestStateQuery,
  (s: URLSearchParams) => void,
  URLSearchParams,
] => {
  const { httpJson } = useHttp();

  const [searchParams, setSearchParams] = useState<URLSearchParams>(
    _searchParams || new URLSearchParams(),
  );

  const params = new URLSearchParams(searchParams);
  const query = params.toString().replaceAll('+', ' ');

  const get = useCallback(
    async () =>
      httpJson<ApiResponse<ElasticSearchResponse<Article>>>(
        `/api/articles?search=${encodeURIComponent(query)}`,
        buildGetRequest(),
      ),
    [query],
  );

  const { data, isPending, isSuccess, isError } = useQuery<
    ApiResponse<ElasticSearchResponse<Article>>
  >({
    queryKey: [ARTICLES_QUERY, query],
    queryFn: get,

    retry: false,
    refetchOnWindowFocus: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
  });

  return [data, { isLoading: isPending, isSuccess, isError }, setSearchParams, searchParams];
};

export default useArticles;
